import { Box } from '@mantine/core'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { AppLayout } from './components/layout/AppLayout'
import { SiteFooter } from './components/layout/SiteFooter'
import { ProtectedRoute } from './components/common/ProtectedRoute'
import { Home } from './pages/landing/Home'
import { Land } from './pages/landing/Land'
import { AboutUs } from './pages/landing/AboutUs'
import { Contact } from './pages/landing/Contact'
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { OnboardingPage } from './pages/onboarding/OnboardingPage'
import { StudentDashboard } from './pages/dashboard/StudentDashboard'
import { TutorDashboard } from './pages/dashboard/TutorDashboard'
import { AdminDashboard } from './pages/dashboard/AdminDashboard'
import { UserManagement } from './pages/dashboard/UserManagement'
import { VocabLearning } from './pages/vocabulary/VocabLearning'
import { VocabReview } from './pages/vocabulary/VocabReview'
import { VocabProgress } from './pages/vocabulary/VocabProgress'
import { VocabTest } from './pages/vocabulary/VocabTest'
import { PracticeSetup } from './pages/practice/PracticeSetup'
import { PracticeSession } from './pages/practice/PracticeSession'
import { RosterList } from './pages/roster/RosterList'
import { RosterDetail } from './pages/roster/RosterDetail'
import { AssignmentList } from './pages/assignments/AssignmentList'
import { AssignmentCreate } from './pages/assignments/AssignmentCreate'
import { AssignmentDetail } from './pages/assignments/AssignmentDetail'
import { StudentPractice } from './pages/assignments/StudentPractice'
import { QuizReview } from './pages/assignments/QuizReview'
import { TutorGrading } from './pages/assignments/TutorGrading'
import { QuestionManager } from './pages/questions/QuestionManager'
import { PaymentPage } from './pages/payment/PaymentPage'
import { SubscriptionPage } from './pages/payment/SubscriptionPage'
import { StudyPlan } from './pages/study-plan/StudyPlan'
import { MockMgr } from './pages/evaluation/MockMgr'
import { EvaluationDetail } from './pages/evaluation/EvaluationDetail'
import { SatEvaluation } from './pages/evaluation/SatEvaluation'
import { CourseList } from './pages/courses/CourseList'
import { CourseViewer } from './pages/courses/CourseViewer'
import { PlanCreation } from './pages/plans/PlanCreation'
import { PlanMgr } from './pages/plans/PlanMgr'
import { SatPlanMgr } from './pages/plans/SatPlanMgr'
import { ContentMgr } from './pages/content/ContentMgr'
import { PrivacyPolicy } from './pages/legal/PrivacyPolicy'
import { TermsOfService } from './pages/legal/TermsOfService'
import { VerifyUser } from './pages/auth/VerifyUser'
import { ForgotPassword } from './pages/auth/ForgotPassword'
import { ResetPassword } from './pages/auth/ResetPassword'
import { Profile } from './pages/profile/Profile'
import { MaterialDetail } from './pages/material/MaterialDetail'

export default function App() {
  return (
    <AuthProvider>
      <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/land" element={<Land />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify_user" element={<VerifyUser />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route element={<AppLayout />}>
              {/* Student routes */}
              <Route path="/dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
              <Route path="/vocabulary" element={<ProtectedRoute><VocabLearning /></ProtectedRoute>} />
              <Route path="/vocabulary/review" element={<ProtectedRoute><VocabReview /></ProtectedRoute>} />
              <Route path="/vocabulary/progress" element={<ProtectedRoute><VocabProgress /></ProtectedRoute>} />
              <Route path="/vocabulary/test" element={<ProtectedRoute><VocabTest /></ProtectedRoute>} />
              <Route path="/practice" element={<ProtectedRoute><PracticeSetup /></ProtectedRoute>} />
              <Route path="/practice/session/:sessionId" element={<ProtectedRoute><PracticeSession /></ProtectedRoute>} />
              <Route path="/assignments" element={<ProtectedRoute><AssignmentList /></ProtectedRoute>} />
              <Route path="/assignments/:id" element={<ProtectedRoute><AssignmentDetail /></ProtectedRoute>} />
              <Route path="/assignments/:id/practice" element={<ProtectedRoute><StudentPractice /></ProtectedRoute>} />
              <Route path="/student/review/:assignmentId" element={<ProtectedRoute><QuizReview /></ProtectedRoute>} />
              <Route path="/study-plan" element={<ProtectedRoute><StudyPlan /></ProtectedRoute>} />
              <Route path="/watch/:id" element={<ProtectedRoute><MaterialDetail /></ProtectedRoute>} />
              <Route
                path="/study-plan/assignments/create"
                element={
                  <ProtectedRoute roles={['student']}>
                    <AssignmentCreate />
                  </ProtectedRoute>
                }
              />
              <Route path="/courses" element={<ProtectedRoute><CourseList /></ProtectedRoute>} />
              <Route path="/courses/:slug" element={<ProtectedRoute><CourseViewer /></ProtectedRoute>} />
              <Route path="/student/evaluate/:uuid/:templateId?" element={<ProtectedRoute><SatEvaluation /></ProtectedRoute>} />
              <Route path="/student/evaluation-result/:id" element={<ProtectedRoute><EvaluationDetail /></ProtectedRoute>} />

              {/* Tutor routes */}
              <Route path="/tutor/dashboard" element={<ProtectedRoute roles={['tutor', 'god']}><TutorDashboard /></ProtectedRoute>} />
              <Route path="/tutor/rosters" element={<ProtectedRoute roles={['tutor', 'god']}><RosterList /></ProtectedRoute>} />
              <Route path="/tutor/rosters/:id" element={<ProtectedRoute roles={['tutor', 'god']}><RosterDetail /></ProtectedRoute>} />
              <Route path="/tutor/assignments" element={<ProtectedRoute roles={['tutor', 'god']}><AssignmentList /></ProtectedRoute>} />
              <Route path="/tutor/assignments/create" element={<ProtectedRoute roles={['tutor', 'god']}><AssignmentCreate /></ProtectedRoute>} />
              <Route path="/tutor/grading" element={<ProtectedRoute roles={['tutor', 'god']}><TutorGrading /></ProtectedRoute>} />
              <Route path="/tutor/mock" element={<ProtectedRoute roles={['tutor', 'god']}><MockMgr /></ProtectedRoute>} />
              <Route path="/tutor/mock/:id" element={<ProtectedRoute roles={['tutor', 'god']}><EvaluationDetail /></ProtectedRoute>} />
              <Route path="/tutor/plans" element={<ProtectedRoute roles={['tutor', 'god']}><PlanCreation /></ProtectedRoute>} />
              <Route path="/tutor/plans/:planId" element={<ProtectedRoute roles={['tutor', 'god']}><PlanMgr /></ProtectedRoute>} />

              {/* Admin routes */}
              <Route path="/admin/dashboard" element={<ProtectedRoute roles={['god']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute roles={['god']}><UserManagement /></ProtectedRoute>} />
              <Route path="/admin/questions" element={<ProtectedRoute roles={['god']}><QuestionManager /></ProtectedRoute>} />
              <Route path="/admin/sat-plans" element={<ProtectedRoute roles={['god']}><SatPlanMgr /></ProtectedRoute>} />
              <Route path="/admin/vocabulary" element={<ProtectedRoute roles={['god']}><QuestionManager /></ProtectedRoute>} />
              <Route path="/admin/content" element={<ProtectedRoute roles={['god']}><ContentMgr /></ProtectedRoute>} />

              {/* Payment */}
              <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
              <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />

              {/* Profile */}
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            </Route>
          </Routes>
        </Box>
        <SiteFooter />
      </Box>
    </AuthProvider>
  )
}
